import { dec, plural, ZERO } from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { Archive, ArchiveRestore, Package, Trash2 } from "lucide-react";
import { useState } from "react";
import { type Item, send, useAllItems, useChange } from "../api";
import { CategoryChip, Dialog, DialogForm, ErrorNote } from "../components/ui";
import { showOnHand } from "../format";
import { PackagingDialog } from "../forms/PackagingDialog";
import { useNotify } from "../notify";

type Action = { kind: "delete" | "archive" | "restore"; item: Item };

/**
 * Deliberately out of the way (Tyler, 2026-09-19): removing an item is for undoing a mistake, not part
 * of daily use. An item with no history can be deleted; anything with recorded changes can only be
 * archived, because the movement log is never edited (invariant 1). Archiving hides it everywhere;
 * adding it again from search restores it with its history.
 *
 * Packaging sits beside it (Tyler, 2026-10-04): what each kind of item comes in is set once and left
 * alone too. The list itself is kept short: an item's name opens it in a new tab, where its stock and
 * history are, rather than repeating them here.
 */
export function InventoryManagement() {
  const [open, setOpen] = useState(false);
  const [packaging, setPackaging] = useState(false);
  const [action, setAction] = useState<Action | null>(null);
  const { data: items = [], isLoading } = useAllItems();
  return (
    <section className="section">
      <div className="section-header" style={{ cursor: "default" }}>
        <h2>Inventory management</h2>
        <span className="aside">
          {items.length} {plural("item", String(items.length))}
        </span>
      </div>
      <div className="section-body stack">
        <p className="muted" style={{ margin: 0 }}>
          Remove items added by mistake, or archive ones you no longer stock. Removing is permanent.
          Packaging is what each kind of item comes in, which invoices are read against.
        </p>
        <div className="row">
          {open ? null : (
            <button type="button" className="button" onClick={() => setOpen(true)}>
              Show all inventory items
            </button>
          )}
          <button type="button" className="button" onClick={() => setPackaging(true)}>
            <Package size={16} /> Packaging
          </button>
        </div>
        {open && isLoading ? <p className="muted">Loading</p> : null}
        {open && items.length > 0 ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Status</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <span className="row" style={{ flexWrap: "nowrap" }}>
                        <Link
                          to="/items/$itemId"
                          params={{ itemId: i.id }}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <strong>{i.displayName}</strong>
                        </Link>
                        <CategoryChip category={i.category} />
                      </span>
                    </td>
                    <td>{i.archived ? <span className="chip plain">Archived</span> : "Active"}</td>
                    <td className="actions">
                      {i.archived ? (
                        <button
                          type="button"
                          className="link-button row"
                          style={{ gap: 4 }}
                          onClick={() => setAction({ kind: "restore", item: i })}
                        >
                          <ArchiveRestore size={14} /> Restore
                        </button>
                      ) : i.historyCount === 0 ? (
                        <button
                          type="button"
                          className="link-button row danger-link"
                          style={{ gap: 4 }}
                          onClick={() => setAction({ kind: "delete", item: i })}
                        >
                          <Trash2 size={14} /> Remove
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="link-button row"
                          style={{ gap: 4 }}
                          onClick={() => setAction({ kind: "archive", item: i })}
                        >
                          <Archive size={14} /> Archive
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {open && !isLoading && items.length === 0 ? (
          <p className="muted">There is nothing in your inventory.</p>
        ) : null}
      </div>
      <ConfirmDialog action={action} onClose={() => setAction(null)} />
      <PackagingDialog open={packaging} onClose={() => setPackaging(false)} />
    </section>
  );
}

/** Each action's dialog title, its button, and what is said once it is done. */
const WORDS = {
  delete: { title: "Remove item", verb: "Remove it", done: "removed" },
  archive: { title: "Archive item", verb: "Archive it", done: "archived" },
  restore: { title: "Restore item", verb: "Restore it", done: "restored" },
};

function ConfirmDialog({ action, onClose }: { action: Action | null; onClose: () => void }) {
  return (
    <Dialog open={action !== null} onClose={onClose} title={action ? WORDS[action.kind].title : ""}>
      {action ? <ConfirmForm action={action} onDone={onClose} /> : null}
    </Dialog>
  );
}

function ConfirmForm({ action, onDone }: { action: Action; onDone: () => void }) {
  const notify = useNotify();
  const { item } = action;
  const change = useChange<undefined, unknown>(
    (_, key) =>
      action.kind === "delete"
        ? send.deleteItem(item.id, key)
        : send.patchItem({ id: item.id, patch: { archived: action.kind === "archive" } }, key),
    {
      onSuccess: () => {
        notify(`${item.displayName} ${WORDS[action.kind].done}`);
        onDone();
      },
    },
  );
  const hasStock = !dec(item.onHand.quantity).eq(ZERO);
  return (
    <DialogForm
      onSubmit={() => change.mutate(undefined)}
      footer={
        <>
          <button type="button" className="button" onClick={onDone}>
            Cancel
          </button>
          <button
            type="submit"
            className={`button ${action.kind === "delete" ? "danger-fill" : "primary"}`}
            disabled={change.isPending}
          >
            {WORDS[action.kind].verb}
          </button>
        </>
      }
    >
      {action.kind === "delete" ? (
        <>
          <div className="notice warn" role="alert">
            This is permanent. It cannot be undone.
          </div>
          <p style={{ margin: 0 }}>
            <strong>{item.displayName}</strong> will be removed from your inventory, along with
            everything tied to it here: its specs, its default supplier and SKU, and its settings.
            Nothing has been received, used or counted for it, so no history is lost.
          </p>
          <p className="muted" style={{ margin: 0 }}>
            If it came from the catalog, you can search for it and add it again later.
          </p>
        </>
      ) : null}
      {action.kind === "archive" ? (
        <>
          <p style={{ margin: 0 }}>
            <strong>{item.displayName}</strong> has {item.historyCount} recorded changes, so it
            cannot be deleted: the stock history is never erased. Archiving hides it from your
            inventory, search results and forms.
          </p>
          {hasStock ? (
            <div className="notice warn">
              {showOnHand(item, "unit")} is still on hand and will stay recorded against it.
            </div>
          ) : null}
          <p className="muted" style={{ margin: 0 }}>
            Restore it here, or add it again from search, and it comes back with its history.
          </p>
        </>
      ) : null}
      {action.kind === "restore" ? (
        <p style={{ margin: 0 }}>
          <strong>{item.displayName}</strong> goes back into your inventory with its history and
          stock.
        </p>
      ) : null}
      <ErrorNote error={change.error} />
    </DialogForm>
  );
}
